import React, { useState } from 'react';
import { Product, Store, Menu } from '../services/orderLinkAPI';
import { hasSelectableAttributes } from '../utils/productUtils';
import { getDirectImageUrl } from '../utils/imageUrl';
import ProductAttributesModal from './ProductAttributesModal';

interface ProductGridProps {
  products: Product[];
  onAddToCart: (
    product: Product,
    quantity: number,
    attributes?: Record<string, any>,
    finalPrice?: number
  ) => void;
  cartItems: Map<string, number>; // product_id -> quantity
  store?: Store;
  menu: Menu;
  productRefs?: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  highlightedProductId?: string | null;
}

const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  onAddToCart,
  cartItems,
  store,
  menu,
  productRefs,
  highlightedProductId
}) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleAddToCart = (product: Product) => {
    // Check if product has selectable attributes
    if (!product.is_combo && hasSelectableAttributes(product)) {
      // Open modal for attribute selection
      setSelectedProduct(product);
      setIsModalOpen(true);
    } else {
      // Add directly without attributes - always quantity 1
      onAddToCart(product, 1);
    }
  };

  const handleAddFromModal = (
    product: Product,
    quantity: number,
    attributes: Record<string, any>,
    finalPrice: number
  ) => {
    onAddToCart(product, quantity, attributes, finalPrice);
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const getCartQuantity = (productId: string): number => {
    return cartItems.get(productId) || 0;
  };

  // Normalize text: lowercase with first letter capitalized
  const normalizeText = (text: string): string => {
    if (!text) return '';
    const lowerText = text.toLowerCase();
    return lowerText.charAt(0).toUpperCase() + lowerText.slice(1);
  };

  // Get description from string-type product_type_inputs
  const getProductDescription = (product: Product): string | null => {
    if (product.description) {
      const included = product.combo_items?.map(item => `${item.quantity}× ${item.name}`).join(', ');
      return included ? `${product.description} · Inclui: ${included}` : product.description;
    }
    if (!product.product_type?.product_type_inputs || !product.attributes) {
      return null;
    }

    // Filter inputs that are type "string"
    const stringInputs = product.product_type.product_type_inputs.filter(
      (input: any) => input.input_type === 'string'
    );

    if (stringInputs.length === 0) {
      return null;
    }

    // Get the first string input value
    const firstStringInput = stringInputs[0];
    const value = product.attributes[firstStringInput.name];

    if (!value || typeof value !== 'string') {
      return null;
    }

    return normalizeText(value);
  };

  if (products.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Nenhum produto disponível nesta categoria
      </div>
    );
  }

  // Flatten menu to get all products for PizzaPartSelectorModal
  const allProducts = Array.from(new Map(
    Object.values(menu).flat().map(product => [product.id, product])
  ).values());

  return (
    <>
      {/* Attributes Modal */}
      {selectedProduct && (
        <ProductAttributesModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
          store={store}
          availableProducts={allProducts}
          onAdd={handleAddFromModal}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {products.map((product) => {
          const isHighlighted = highlightedProductId === product.id;
          return (
            <div
              key={product.id}
              ref={(el) => {
                if (productRefs) {
                  productRefs.current[product.id] = el;
                }
              }}
              className={`bg-white flex flex-row items-stretch rounded-md border-[1.5px] border-gray-900 transition-all duration-300 ${
                isHighlighted ? 'ring-2 ring-offset-2' : ''
              }`}
              style={isHighlighted ? { '--tw-ring-color': 'var(--bg-color)' } as React.CSSProperties : {}}
            >
          {/* Product Info - Left Side */}
          <div className="flex-1 min-w-0 p-3.5 flex flex-col">
            <h3 className="text-[17px] leading-tight font-bold text-gray-900 mb-1 line-clamp-2">
              {product.name}
            </h3>

            {/* Description from string-type inputs */}
            {getProductDescription(product) && (
              <p className="text-[14px] leading-snug text-gray-600 mb-2 line-clamp-2">
                {getProductDescription(product)}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-auto mb-2.5">
              <p className="text-[19px] font-extrabold tracking-tight whitespace-nowrap" style={{ color: 'var(--text-color)' }}>
                R$ {product.price.toFixed(2).replace('.', ',')}
              </p>
              {product.eh_promocao && (
                <span className="px-1.5 py-0.5 text-[11px] font-mono font-medium text-white rounded-sm"
                  style={{ backgroundColor: 'var(--badge-color)' }}
                >
                  promo
                </span>
              )}
            </div>

            {/* Add Button or Quantity Control */}
            {getCartQuantity(product.id) > 0 ? (
              <div className="inline-flex self-start items-center rounded-md border-[1.5px] border-gray-900 overflow-hidden">
                <button
                  onClick={(e: any) => {
                    e.stopPropagation();
                    onAddToCart(product, -1);
                  }}
                  aria-label={`Remover ${product.name}`}
                  className="w-11 h-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <span className="text-[20px] font-bold leading-none">−</span>
                </button>
                <span className="text-[16px] font-bold text-gray-900 min-w-[32px] text-center px-1">
                  {getCartQuantity(product.id)}
                </span>
                <button
                  onClick={(e: any) => {
                    e.stopPropagation();
                    handleAddToCart(product);
                  }}
                  aria-label={`Adicionar mais ${product.name}`}
                  className="w-11 h-10 flex items-center justify-center text-white transition-colors hover:opacity-90"
                  style={{ backgroundColor: 'var(--bg-color)' }}
                >
                  <span className="text-[20px] font-bold leading-none">+</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleAddToCart(product)}
                className="self-start h-10 px-4 text-[14px] font-bold text-white rounded-md border-[1.5px] border-gray-900 shadow-dura transition-all active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
                style={{ backgroundColor: 'var(--bg-color)' }}
              >
                Adicionar
              </button>
            )}
          </div>

          {/* Product Image - Right Side */}
          {product.image_path && (
            <div className="w-[116px] sm:w-[148px] flex-shrink-0 border-l-[1.5px] border-gray-900 rounded-r-[5px] overflow-hidden bg-gray-100">
              <img
                src={getDirectImageUrl(product.image_path)}
                alt={product.name}
                loading="lazy"
                className="w-full h-full object-cover"
                onError={(e: any) => {
                  e.currentTarget.parentElement.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      );
        })}
      </div>
    </>
  );
};

export default ProductGrid;
